import type { components } from "../../api/schema";
import { InlineError, Menu, Stack, type MenuItem } from "../../ui";
import { RowConfirm } from "../shared/RowConfirm";

type TemplateResponse = components["schemas"]["TemplateResponse"];

export interface TemplateRowActionHandlers {
  deleteError: string | null;
  deleteTemplateId: string | null;
  isDeletePending: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: (template: TemplateResponse) => void;
  onOpenEdit: (templateId: string) => void;
  onOpenInTranscripts: (batchId: string) => void;
  onPauseSchedule: (template: TemplateResponse) => void;
  onResumeSchedule: (template: TemplateResponse) => void;
  onRun: (templateId: string, agentId: string) => void;
  onStartDelete: (template: TemplateResponse) => void;
  onToggleLineage: (templateId: string) => void;
  pausedScheduleTemplateId: string | null;
  resumedScheduleTemplateId: string | null;
  expandedTemplateId: string | null;
}

export interface TemplateRowActionsProps {
  handlers: TemplateRowActionHandlers;
  template: TemplateResponse;
}

export function TemplateRowActions({
  handlers,
  template,
}: TemplateRowActionsProps) {
  const isConfirming = handlers.deleteTemplateId === template.id;
  const isExpanded = handlers.expandedTemplateId === template.id;
  const confirm = (
    <RowConfirm
      confirmLabel="Confirm delete"
      isConfirming={isConfirming}
      isPending={isConfirming && handlers.isDeletePending}
      isTriggerDisabled={handlers.isDeletePending}
      onCancel={handlers.onCancelDelete}
      onConfirm={() => {
        handlers.onConfirmDelete(template);
      }}
      onStart={() => {
        handlers.onStartDelete(template);
      }}
      pendingLabel="Deleting..."
      sentence={`Delete ${template.display_name}? This removes the saved template.`}
      triggerLabel="Delete"
    />
  );
  if (isConfirming) {
    return (
      <Stack gap={2}>
        {confirm}
        {handlers.deleteError ? (
          <InlineError>{handlers.deleteError}</InlineError>
        ) : null}
      </Stack>
    );
  }
  // Four buttons inline cost ~320px of the row, which pushed the whole Actions
  // cell off the right edge of the table. §4b.1: overflow actions collapse into
  // a Menu — the `ScenarioActions` row precedent.
  const items: MenuItem[] = [
    {
      id: "run",
      label: "Run",
      onSelect: () => {
        handlers.onRun(template.id, template.agent_id);
      },
    },
    {
      id: "runs",
      label: isExpanded ? "Hide runs" : "Show runs",
      onSelect: () => {
        handlers.onToggleLineage(template.id);
      },
    },
    {
      id: "edit",
      label: "Edit",
      onSelect: () => {
        handlers.onOpenEdit(template.id);
      },
    },
    ...(template.schedule === null || template.schedule.trim().length === 0
      ? []
      : template.schedule_paused_at === null
        ? [
            {
              disabled:
                handlers.pausedScheduleTemplateId === template.id ||
                handlers.resumedScheduleTemplateId !== null,
              id: "pause-schedule",
              label:
                handlers.pausedScheduleTemplateId === template.id
                  ? "Pausing…"
                  : "Pause schedule",
              onSelect: () => {
                handlers.onPauseSchedule(template);
              },
            },
          ]
        : [
            {
              disabled:
                handlers.resumedScheduleTemplateId === template.id ||
                handlers.pausedScheduleTemplateId !== null,
              id: "resume-schedule",
              label:
                handlers.resumedScheduleTemplateId === template.id
                  ? "Resuming…"
                  : "Resume schedule",
              onSelect: () => {
                handlers.onResumeSchedule(template);
              },
            },
          ]),
    {
      danger: true,
      disabled: handlers.isDeletePending,
      id: "delete",
      label: "Delete",
      onSelect: () => {
        handlers.onStartDelete(template);
      },
    },
  ];

  return <Menu items={items} label="Actions" />;
}
